import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Wrapper, Header, Footer } from '../../../general_components/forms/Form';
import { API_ROOT, sendRequest } from '../../../supplementary_components/requests';
import { addNotification } from '../../../supplementary_components/store';
import { can, canSeeLoadRates, storeCustomizations } from '../../../supplementary_components/userDataDelegate';
import {
    DEFAULT_LOAD_DISPATCH_INFO,
    DISPATCH_FIELDS,
    DISPATCH_FIELDS_BY_KEY,
    DISPATCH_SECTIONS,
    LOAD_DISPATCH_INFO_KEY,
    buildSampleDispatchData,
    canPlaceDispatchField as canPlaceField,
    isDispatchFieldExcluded,
    isDefaultLoadDispatchInfo,
    parseDispatchTemplate,
    renderLoadDispatchInfo,
    retrieveLoadDispatchInfoFormat,
} from './loadDispatchInfoFormat';
import styles from './loadDispatchInfoPopup.module.scss';

const FIELD_MIME = 'application/x-surftms-dispatch-field';

const SECTION_LABELS = {
    header: { title: 'Header' },
    body: { hint: 'Repeated for every stop' },
    footer: { title: 'Footer' },
};

const createFieldChip = (key) => {
    const field = DISPATCH_FIELDS_BY_KEY[key];
    const chip = document.createElement('span');
    chip.contentEditable = 'false';
    chip.draggable = true;
    chip.dataset.field = key;
    if (field.locked) chip.dataset.locked = 'true';
    chip.className = `${styles.fieldChip} ${field.locked ? styles.fieldChipLocked : ''}`;
    chip.textContent = field.label;
    chip.title = field.locked ? 'Can be moved within the body, cannot be removed' : 'Drag to move, drag out to remove';
    return chip;
};

const lastMeaningfulChild = (el) => {
    let node = el.lastChild;
    while (node && node.nodeType === Node.TEXT_NODE && node.nodeValue === '') node = node.previousSibling;
    return node;
};

// A <br> that ends the editor does not render an empty line, so it is never serialized.
// Whenever content must end with a real line break, one extra <br> follows it.
const ensureTrailingLineVisible = (el) => {
    const last = lastMeaningfulChild(el);
    if (last && last.nodeName === 'BR') el.appendChild(document.createElement('br'));
};

const fillEditor = (el, text) => {
    el.replaceChildren();
    for (const part of parseDispatchTemplate(text)) {
        if (part.type === 'field') {
            el.appendChild(createFieldChip(part.key));
            continue;
        }
        part.value.split('\n').forEach((segment, index) => {
            if (index > 0) el.appendChild(document.createElement('br'));
            if (segment) el.appendChild(document.createTextNode(segment));
        });
    }
    ensureTrailingLineVisible(el);
};

const serializeEditor = (el) => {
    const trailing = lastMeaningfulChild(el);
    let out = '';
    const walk = (node) => {
        for (const child of node.childNodes) {
            if (child.nodeType === Node.TEXT_NODE) {
                out += child.nodeValue.replace(/\u00A0/g, ' ').replace(/\u200B/g, '');
                continue;
            }
            if (child.nodeType !== Node.ELEMENT_NODE) continue;
            if (child.dataset.field) {
                out += `{${child.dataset.field}}`;
                continue;
            }
            if (child.nodeName === 'BR') {
                if (child !== trailing) out += '\n';
                continue;
            }
            if ((child.nodeName === 'DIV' || child.nodeName === 'P') && out !== '' && !out.endsWith('\n')) {
                out += '\n';
            }
            walk(child);
        }
    };
    walk(el);
    return out;
};

const endOfContentRange = (el) => {
    const range = document.createRange();
    const last = lastMeaningfulChild(el);
    if (last && last.nodeName === 'BR') {
        range.setStartBefore(last);
    } else {
        range.selectNodeContents(el);
        range.collapse(false);
    }
    range.collapse(true);
    return range;
};

const dropRangeFromPoint = (el, x, y) => {
    let range = null;
    if (document.caretRangeFromPoint) {
        range = document.caretRangeFromPoint(x, y);
    } else if (document.caretPositionFromPoint) {
        const position = document.caretPositionFromPoint(x, y);
        if (position) {
            range = document.createRange();
            range.setStart(position.offsetNode, position.offset);
            range.collapse(true);
        }
    }
    if (range) {
        const container = range.startContainer.nodeType === Node.ELEMENT_NODE
            ? range.startContainer
            : range.startContainer.parentElement;
        const chip = container?.closest?.('[data-field]');
        if (chip && el.contains(chip)) {
            const rect = chip.getBoundingClientRect();
            range = document.createRange();
            if (x < rect.left + rect.width / 2) range.setStartBefore(chip);
            else range.setStartAfter(chip);
            range.collapse(true);
        }
    }
    if (!range || !el.contains(range.startContainer)) return endOfContentRange(el);
    return range;
};

const caretRectForRange = (el, range) => {
    const probe = document.createElement('span');
    probe.textContent = '\u200B';
    range.insertNode(probe);
    const rect = probe.getBoundingClientRect();
    probe.remove();
    el.normalize();
    return rect;
};

const insertNodesAtSelection = (el, nodes) => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0 || nodes.length === 0) return;
    const range = selection.getRangeAt(0);
    if (!el.contains(range.commonAncestorContainer)) return;
    range.deleteContents();
    const fragment = document.createDocumentFragment();
    nodes.forEach((node) => fragment.appendChild(node));
    const lastNode = nodes[nodes.length - 1];
    range.insertNode(fragment);
    if (lastNode.nodeName === 'BR' && lastMeaningfulChild(el) === lastNode) {
        el.appendChild(document.createElement('br'));
    }
    range.setStartAfter(lastNode);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);
};

const textToNodes = (text) => {
    const nodes = [];
    text.replace(/\r\n?/g, '\n').split('\n').forEach((segment, index) => {
        if (index > 0) nodes.push(document.createElement('br'));
        if (segment) nodes.push(document.createTextNode(segment));
    });
    return nodes;
};

const rangeTouchesLockedField = (el, staticRanges) => {
    const locked = el.querySelectorAll('[data-locked="true"]');
    if (locked.length === 0) return false;
    for (const staticRange of staticRanges) {
        const range = document.createRange();
        try {
            range.setStart(staticRange.startContainer, staticRange.startOffset);
            range.setEnd(staticRange.endContainer, staticRange.endOffset);
        } catch (_) {
            continue;
        }
        for (const chip of locked) {
            if (range.intersectsNode(chip)) return true;
        }
    }
    return false;
};

/**
 * Keeps the editors consistent after browser edits (undo, deletes, drops): each field at most
 * once, in a section it belongs to, never next to a field it excludes, and locked fields always
 * present in the body.
 */
const repairEditors = (editors) => {
    const seen = new Set();
    for (const section of DISPATCH_SECTIONS) {
        const el = editors[section];
        for (const chip of [...el.querySelectorAll('[data-field]')]) {
            const field = DISPATCH_FIELDS_BY_KEY[chip.dataset.field];
            if (!field || seen.has(field.key) || !canPlaceField(field.key, section) || isDispatchFieldExcluded(field.key, seen)) {
                chip.remove();
                continue;
            }
            seen.add(field.key);
        }
    }
    for (const field of DISPATCH_FIELDS) {
        if (field.locked && !seen.has(field.key)) {
            endOfContentRange(editors.body).insertNode(createFieldChip(field.key));
        }
    }
};

export const LoadDispatchInfoPopup = ({ closePopup }) => {
    const canSeeDriverPayout = can('DRIVER_PAYOUTS', 'VIEW') && canSeeLoadRates();
    const initialFormat = useMemo(() => retrieveLoadDispatchInfoFormat(), []);
    const sampleData = useMemo(() => buildSampleDispatchData(), []);

    const [format, setFormat] = useState(initialFormat);
    const [saving, setSaving] = useState(false);
    const [marker, setMarker] = useState(null);
    const [draggingKey, setDraggingKey] = useState(null);

    const headerRef = useRef(null);
    const bodyRef = useRef(null);
    const footerRef = useRef(null);
    const sectionRefs = useMemo(() => ({ header: headerRef, body: bodyRef, footer: footerRef }), []);
    const wrapperRefs = useRef({});
    const dragRef = useRef(null);

    const editors = () => ({ header: headerRef.current, body: bodyRef.current, footer: footerRef.current });

    const syncFormat = () => {
        const els = editors();
        repairEditors(els);
        setFormat((current) => ({
            ...current,
            header: serializeEditor(els.header),
            body: serializeEditor(els.body),
            footer: serializeEditor(els.footer),
        }));
    };

    const fillAll = (nextFormat) => {
        const els = editors();
        for (const section of DISPATCH_SECTIONS) fillEditor(els[section], nextFormat[section]);
        setFormat((current) => ({ ...current, highValueWarning: nextFormat.highValueWarning }));
        syncFormat();
    };

    useEffect(() => {
        fillAll(initialFormat);
        const els = editors();
        const blockLockedEdits = (event) => {
            const el = event.currentTarget;
            if (event.inputType.startsWith('format')) {
                event.preventDefault();
                return;
            }
            const targetRanges = event.getTargetRanges ? event.getTargetRanges() : [];
            const selection = window.getSelection();
            const ranges = targetRanges.length > 0
                ? targetRanges
                : selection && selection.rangeCount > 0 ? [selection.getRangeAt(0)] : [];
            if (rangeTouchesLockedField(el, ranges)) event.preventDefault();
        };
        Object.values(els).forEach((el) => el.addEventListener('beforeinput', blockLockedEdits));
        return () => Object.values(els).forEach((el) => el.removeEventListener('beforeinput', blockLockedEdits));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const usedFields = useMemo(() => {
        const used = new Set();
        for (const section of DISPATCH_SECTIONS) {
            parseDispatchTemplate(format[section]).forEach((p) => p.type === 'field' && used.add(p.key));
        }
        return used;
    }, [format]);

    const paletteFields = DISPATCH_FIELDS.filter((field) => !field.locked
        && (!field.requiresDriverPayout || canSeeDriverPayout)
        && !isDispatchFieldExcluded(field.key, usedFields));

    const preview = useMemo(() => renderLoadDispatchInfo(format, sampleData), [format, sampleData]);

    const finishDrag = () => {
        dragRef.current?.sourceChip?.classList.remove(styles.fieldChipDragging);
        dragRef.current = null;
        setMarker(null);
        setDraggingKey(null);
    };

    const startDrag = (event, key, sourceChip) => {
        dragRef.current = { key, sourceChip };
        setDraggingKey(key);
        event.dataTransfer.effectAllowed = sourceChip ? 'move' : 'copy';
        event.dataTransfer.setData(FIELD_MIME, key);
        event.dataTransfer.setData('text/plain', '');
        if (sourceChip) {
            requestAnimationFrame(() => {
                if (dragRef.current?.sourceChip === sourceChip) sourceChip.classList.add(styles.fieldChipDragging);
            });
        }
    };

    const onPaletteDragStart = (event, key) => {
        if (usedFields.has(key) || isDispatchFieldExcluded(key, usedFields)) {
            event.preventDefault();
            return;
        }
        startDrag(event, key, null);
    };

    const onEditorDragStart = (event) => {
        const chip = event.target?.closest?.('[data-field]');
        if (!chip) {
            event.preventDefault();
            return;
        }
        event.stopPropagation();
        startDrag(event, chip.dataset.field, chip);
    };

    const onEditorDragOver = (event, section) => {
        const drag = dragRef.current;
        event.preventDefault();
        event.stopPropagation();
        if (!drag || !canPlaceField(drag.key, section)) {
            event.dataTransfer.dropEffect = 'none';
            setMarker(null);
            return;
        }
        event.dataTransfer.dropEffect = drag.sourceChip ? 'move' : 'copy';
        const el = sectionRefs[section].current;
        const wrapper = wrapperRefs.current[section];
        const rect = caretRectForRange(el, dropRangeFromPoint(el, event.clientX, event.clientY));
        const wrapperRect = wrapper.getBoundingClientRect();
        setMarker({
            section,
            left: rect.left - wrapperRect.left,
            top: rect.top - wrapperRect.top,
            height: rect.height || 18,
        });
    };

    const onEditorDragLeave = (event, section) => {
        if (event.relatedTarget && event.currentTarget.contains(event.relatedTarget)) return;
        setMarker((current) => (current?.section === section ? null : current));
    };

    const onEditorDrop = (event, section) => {
        event.preventDefault();
        event.stopPropagation();
        const drag = dragRef.current;
        if (drag && canPlaceField(drag.key, section)) {
            const el = sectionRefs[section].current;
            const range = dropRangeFromPoint(el, event.clientX, event.clientY);
            const chip = createFieldChip(drag.key);
            range.insertNode(chip);
            if (drag.sourceChip) drag.sourceChip.remove();
            syncFormat();
        }
        finishDrag();
    };

    const onOutsideDragOver = (event) => {
        const drag = dragRef.current;
        if (drag?.sourceChip && !DISPATCH_FIELDS_BY_KEY[drag.key]?.locked) {
            event.preventDefault();
            event.dataTransfer.dropEffect = 'move';
        }
        setMarker(null);
    };

    const onOutsideDrop = (event) => {
        event.preventDefault();
        const drag = dragRef.current;
        if (drag?.sourceChip && !DISPATCH_FIELDS_BY_KEY[drag.key]?.locked) {
            drag.sourceChip.remove();
            syncFormat();
        }
        finishDrag();
    };

    const onEditorKeyDown = (event) => {
        if (event.key !== 'Enter' || event.nativeEvent.isComposing) return;
        event.preventDefault();
        insertNodesAtSelection(event.currentTarget, [document.createElement('br')]);
        syncFormat();
    };

    const onEditorPaste = (event) => {
        event.preventDefault();
        const text = event.clipboardData?.getData('text/plain') || '';
        if (!text) return;
        insertNodesAtSelection(event.currentTarget, textToNodes(text));
        syncFormat();
    };

    const onSave = () => {
        if (saving) return;
        setSaving(true);
        const value = isDefaultLoadDispatchInfo(format) ? null : format;
        sendRequest(
            'PATCH',
            API_ROOT + 'employees/customizations',
            null,
            (updated) => {
                storeCustomizations(updated);
                setSaving(false);
                addNotification({ message: 'Load Dispatch Info format saved', type: 'success' });
                closePopup();
            },
            (error) => {
                setSaving(false);
                addNotification({
                    message: error?.customError || 'Failed to save Load Dispatch Info format',
                    type: 'alert',
                });
            },
            { [LOAD_DISPATCH_INFO_KEY]: value }
        );
    };

    return (
        <Wrapper>
            <Header className={styles.popupBar}>
                <span>Load Dispatch Info</span>
            </Header>
            <div
                className={styles.content}
                onDragOver={onOutsideDragOver}
                onDrop={onOutsideDrop}
                onDragEnd={finishDrag}
            >
                <div className={styles.editorColumn}>
                    <p className={styles.help}>
                        Type text and spacing directly in each area. Drag fields in to add them, drag them out to
                        remove. Stop fields (PU/DEL, Stop Type, Stop Time, Stop Address) go in the stop area, all
                        others in the header or footer. Stop Time and Stop Address cannot be removed. Lines whose
                        fields are empty for a load are skipped.
                    </p>
                    <div className={styles.palette}>
                        {paletteFields.map((field) => {
                            const used = usedFields.has(field.key);
                            return (
                                <div
                                    key={field.key}
                                    className={`${styles.paletteChip} ${used ? styles.paletteChipUsed : ''} ${
                                        draggingKey === field.key ? styles.paletteChipDragging : ''
                                    }`}
                                    draggable={!used}
                                    onDragStart={(event) => onPaletteDragStart(event, field.key)}
                                    title={used ? 'Already in the format' : 'Drag into the format'}
                                >
                                    {field.label}
                                    {field.note ? <span className={styles.paletteNote}>{field.note}</span> : null}
                                </div>
                            );
                        })}
                    </div>
                    {DISPATCH_SECTIONS.map((section) => (
                        <div key={section} className={styles.sectionBlock}>
                            <div className={styles.sectionLabel}>
                                {SECTION_LABELS[section].title}
                                {SECTION_LABELS[section].hint ? (
                                    <span className={styles.sectionHint}>{SECTION_LABELS[section].hint}</span>
                                ) : null}
                            </div>
                            <div
                                className={styles.editorWrapper}
                                ref={(node) => { wrapperRefs.current[section] = node; }}
                            >
                                <div
                                    ref={sectionRefs[section]}
                                    className={`${styles.editor} ${
                                        draggingKey && !canPlaceField(draggingKey, section) ? styles.editorBlocked : ''
                                    }`}
                                    contentEditable
                                    suppressContentEditableWarning
                                    spellCheck={false}
                                    onInput={syncFormat}
                                    onKeyDown={onEditorKeyDown}
                                    onPaste={onEditorPaste}
                                    onDragStart={onEditorDragStart}
                                    onDragOver={(event) => onEditorDragOver(event, section)}
                                    onDragLeave={(event) => onEditorDragLeave(event, section)}
                                    onDrop={(event) => onEditorDrop(event, section)}
                                />
                                {marker?.section === section ? (
                                    <div
                                        className={styles.dropMarker}
                                        style={{ left: marker.left, top: marker.top, height: marker.height }}
                                        aria-hidden
                                    />
                                ) : null}
                            </div>
                        </div>
                    ))}
                    <div className={styles.sectionBlock}>
                        <label className={styles.sectionLabel} htmlFor="dispatchHighValueWarning">
                            High Value Load Warning
                        </label>
                        <textarea
                            id="dispatchHighValueWarning"
                            className={styles.warningInput}
                            value={format.highValueWarning}
                            maxLength={2000}
                            rows={3}
                            placeholder="Added below the footer for high value loads"
                            onChange={(event) => {
                                const highValueWarning = event.target.value;
                                setFormat((current) => ({ ...current, highValueWarning }));
                            }}
                        />
                    </div>
                    <button type="button" className={styles.resetButton} onClick={() => fillAll(DEFAULT_LOAD_DISPATCH_INFO)}>
                        Reset
                    </button>
                </div>
                <div className={styles.previewColumn}>
                    <div className={styles.sectionLabel}>Preview</div>
                    <div className={styles.preview}>{preview}</div>
                </div>
            </div>
            <Footer className={styles.popupBar}>
                <div className="non-selectable" onClick={() => !saving && closePopup()}>
                    Close
                </div>
                <div className={`non-selectable ${saving ? 'disabled' : ''}`} onClick={onSave}>
                    <input type="button" value={saving ? 'Saving…' : 'Save'} readOnly disabled={saving} />
                </div>
            </Footer>
        </Wrapper>
    );
};
